import React, { useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { usePaginatedService } from '@aph/common-react';
import queryString from 'query-string';
import dayjs from 'dayjs';

import { defaultPageSize } from 'global.constants';
import { IStatisticsSearchFormData } from 'types/form.types';
import {
  IUseSearchService,
  IStatisticsSearchRequest,
  IStatisticsSearchResponse,
} from 'services';
import { setupDateForRequest } from 'helpers/form.helpers';
import { StatisticsSearchInner } from 'search-interfaces';

/**
 * BaseStatisticsSearchRequest
 *
 * @description Base search request for searching statistics publications
 */
const baseStatisticsSearchRequest: IStatisticsSearchRequest = {
  topics: [],
  currentPage: 1,
  pageSize: defaultPageSize
};

/**
 * InitialFormValues
 *
 * @description Initial form values for statistics publication search filter form
 */
const initialFormValues: IStatisticsSearchFormData = {
  searchTerms: '',
  type: null,
  topics: [],
  dateFrom: null,
  dateTo: null
};

/**
 * IStatisticsSearch
 *
 * @description Component properties for the Statistics search component
 */
interface IStatisticsSearch {
  searchService: IUseSearchService;
}

/**
 * StatisticsSearch
 *
 * @description
 */
const StatisticsSearch: React.FC<IStatisticsSearch> = ({ searchService }) => {
  const formRef = useRef<any>();

  const {
    loading,
    currentPage,
    rowCount,
    pageSize,
    results,
    request: currentSearchRequest,
    refreshResults: refreshSearchResults,
    updatePagination,
  } = usePaginatedService<IStatisticsSearchRequest, IStatisticsSearchResponse>(searchService.searchStatistics, baseStatisticsSearchRequest, false);

  // Setup the initial search results we need to first pass the URL params
  useEffect(() => {
    const parsedQueryParameters = queryString.parse(window.location.search);

    const topicsParsed = parsedQueryParameters['topics'] && typeof(parsedQueryParameters['topics']) === 'string'
      ? [parsedQueryParameters['topics']]
      : parsedQueryParameters['topics'] as Array<string> ?? [];
    const dateFrom = typeof parsedQueryParameters['dateFrom'] === 'string' ? dayjs(parsedQueryParameters['dateFrom']) : null;
    const dateTo = typeof parsedQueryParameters['dateTo'] === 'string' ? dayjs(parsedQueryParameters['dateTo']) : null;

    const initialPublicationSearchRequest: IStatisticsSearchRequest = {
      ...baseStatisticsSearchRequest,
      ...parsedQueryParameters,
      topics: topicsParsed
    };

    refreshSearchResults(initialPublicationSearchRequest);

    if (formRef.current) {
      formRef.current.setValues({
        ...initialFormValues,
        ...parsedQueryParameters,
        topics: topicsParsed,
        dateFrom: dateFrom,
        dateTo: dateTo
      });
    }
  }, []);

  /**
   * HandleSubmitSearch
   *
   * @description Form submission handler
   * @param {IStatisticsSearchFormData} values Form values
   * @returns {void}
   */
  const handleSubmitSearch = (values: IStatisticsSearchFormData): void => {
    const updatedSearchRequest: IStatisticsSearchRequest = {
      currentPage: 1,
      pageSize: defaultPageSize,
      topics: values.topics,
      type: values.type
    };

    if (values.searchTerms) {
      updatedSearchRequest.searchTerms = values.searchTerms;
    }

    updatedSearchRequest.dateFrom = setupDateForRequest(values?.dateFrom);
    updatedSearchRequest.dateTo = setupDateForRequest(values?.dateTo);

    refreshSearchResults(updatedSearchRequest);
  };

  return (
    <div className='library-publication-search'>
      <Formik<IStatisticsSearchFormData>
        initialValues={{
          searchTerms: '',
          type: '',
          topics: [],
          dateFrom: null,
          dateTo: null
        }}
        // We don't need to run the submit because we are actually refreshing the results on change of each field
        onSubmit={handleSubmitSearch}
        onReset={() => {
          window.history.replaceState(null, '', '?');
          refreshSearchResults(baseStatisticsSearchRequest);
        }}
        validateOnChange={false}
        validateOnBlur={false}
        innerRef={formRef}
      >
        <StatisticsSearchInner
          searchService={searchService}
          searchResults={{
            loading,
            results,
            currentPage,
            pageSize,
            rowCount
          }}
          currentSearchRequest={currentSearchRequest}
          paginationHandler={updatePagination}
          filterSearchResults={refreshSearchResults}
        />
      </Formik>
    </div>
  );
};

export default StatisticsSearch;
